import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './button.js';

/**
 * Standardized Modal Component
 * @param {Object} props - Modal properties
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback when modal should close
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.title - Modal title
 * @param {string} props.size - Modal size: 'sm', 'md', 'lg', 'xl'
 * @param {boolean} props.showCloseButton - Whether to show close button
 * @param {string} props.className - Additional CSS classes
 */
const Modal = ({
    isOpen,
    onClose,
    children,
    title,
    size = 'md',
    showCloseButton = true,
    className = ''
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle ESC key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen && onClose) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '4xl': 'max-w-4xl'
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-85 flex justify-center items-center z-[9999]"
            onClick={onClose}
            style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                position: 'fixed',
                margin: 0,
                padding: 0
            }}
        >
            <div
                className={`bg-white dark:bg-[#161b22] rounded-lg w-[90%] ${sizeClasses[size]} p-6 shadow-xl border dark:border-[#30363d] max-h-[90vh] flex flex-col ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                {(title || showCloseButton) && (
                    <div className="flex items-center justify-between mb-4 flex-shrink-0">
                        {title && (
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                {title}
                            </h2>
                        )}
                        {showCloseButton && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="p-2"
                                aria-label="Close"
                            >
                                <svg className="h-5 w-5 text-gray-900 dark:text-[#c9d1d9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </Button>
                        )}
                    </div>
                )}
                <div className="flex-1 overflow-y-auto min-h-0" style={{ paddingLeft: '4px', paddingRight: '4px', paddingTop: '4px', paddingBottom: '4px' }}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;

